import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Table, Form, Container, Row, Col, Button } from 'react-bootstrap';
import PaginationComponent from '../Pagination/PaginationComponent';
import { getAllCompanys } from '../../services/Company/companyService';
import CreateCompanyModal from './CreateCompanyModal';
import EditCompanyModal from './EditCompanyModal';
import CompanyFilter from '../Filters/CompanyFilter';
import { FaEdit } from 'react-icons/fa';
import { usePermissions } from '../../hooks/usePermissions';
import { usePermissionsContext } from '../../context/PermissionsContext';

const Company = () => {
  const { checkPermission, ToastComponent, hasPermission } = usePermissions();
  const { permissions } = usePermissionsContext();
  const [persons, setCompanys] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [numberOfElements, setNumberOfElements] = useState(0);
  const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
  const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [filterParams, setFilterParams] = useState({});
  const previousFilterParams = useRef({});

  // Recalcula permissões sempre que o objeto permissions mudar
  const canView = useMemo(() => hasPermission('companyView'), [hasPermission, permissions]);
  const canCreate = useMemo(() => hasPermission('companyCreate'), [hasPermission, permissions]);
  const canUpdate = useMemo(() => hasPermission('companyUpdate'), [hasPermission, permissions]);

  const fetchCompanys = useCallback(async (filters = filterParams, currentPage = page, currentPageSize = pageSize) => {
    if (!canView) return;
    try {
      const data = await getAllCompanys(filters, currentPage - 1, currentPageSize);
      setCompanys(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
      setNumberOfElements(data.numberOfElements);
    } catch (error) {}
  }, [filterParams, page, pageSize, canView]);

  useEffect(() => {
    if (canView) {
      fetchCompanys();
    }
  }, [page, pageSize, fetchCompanys, canView]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(1);
  };

  const handleOpenCreateCompanyModal = () => {
    if (!checkPermission('companyCreate')) {
      return;
    }
    setShowCreateCompanyModal(true);
  };
  const handleCloseCreateCompanyModal = () => setShowCreateCompanyModal(false);

  const handleOpenEditCompanyModal = (company) => {
    if (!checkPermission('companyUpdate')) {
      return;
    }
    setSelectedCompany(company);
    setShowEditCompanyModal(true);
  };
  
  const handleCloseEditCompanyModal = () => {
    setSelectedCompany(null);
    setShowEditCompanyModal(false);
  };
  
  const handleFilter = (filters) => {
    if (!canView) return;
    const filtersChanged = JSON.stringify(previousFilterParams.current) !== JSON.stringify(filters);

    if (filtersChanged) {
      setFilterParams(filters);
      setPage(1);
      fetchCompanys(filters, 1, pageSize);
      previousFilterParams.current = filters;
    }
  };

  const handleClearFilter = () => {
    if (!canView) return;
    setFilterParams({});
    setPage(1);
    fetchCompanys({}, 1, pageSize);
    previousFilterParams.current = {};
  };

  if (!canView) {
    return (
      <>
        <ToastComponent />
        <Container>
          <h3 className="mb-4 mt-2 text-center text-md-start">Empresas / Filiais</h3>
        </Container>
      </>
    );
  }

  return (
    <Container>
      <ToastComponent />
      <h3 className="mb-4 mt-2 text-center text-md-start">Empresas / Filiais</h3>
      <Row className="justify-content-between align-items-center mb-3">
        <Col xs={12} md="auto" className="d-flex align-items-center justify-content-md-end justify-content-center">
          <Form.Label className="me-2 mb-0">Mostrar</Form.Label>
          <Form.Select
            value={pageSize}
            onChange={handlePageSizeChange}
            style={{ width: 'auto' }}
            className="me-2"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Select>
        </Col>
        <Col xs={12} md="auto" className="d-flex justify-content-md-end justify-content-center mt-2 mt-md-0">
          {canCreate && (
            <Button variant="primary" className="button-custom" onClick={handleOpenCreateCompanyModal}>
              Cadastrar Empresa
            </Button>
          )}
        </Col>
      </Row>
      {canView && <CompanyFilter onFilter={handleFilter} onClear={handleClearFilter} />}
      <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover responsive="sm">
          <thead>
            <tr>
              <th>Razão Social</th>
              <th>Documento</th>
              <th>Cidade / UF</th>
              <th style={{textAlign: 'center', width: '50px' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {persons.map((company) => (
              <tr key={company.id}>
                <td>{company.name}</td>
                <td>{company.document}</td>
                <td>{company.city + '/' + company.state}</td>
                <td style={{textAlign: 'center'}}>
                  {canUpdate && (
                    <Button
                      variant="link"
                      onClick={() => handleOpenEditCompanyModal(company)}
                      className="p-0"
                      style={{ fontSize: '1.2rem' }}
                    >
                      <FaEdit className='icon-custom'/>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <PaginationComponent
        totalPages={totalPages}
        currentPage={page}
        onPageChange={handlePageChange}
        totalElements={totalElements}
        size={pageSize}
        pageNumber={page - 1}
        numberOfElements={numberOfElements}
      />

      <CreateCompanyModal
        show={showCreateCompanyModal}
        handleClose={handleCloseCreateCompanyModal}
        refreshCompanies={fetchCompanys}
      />

      <EditCompanyModal
        show={showEditCompanyModal}
        handleClose={handleCloseEditCompanyModal}
        company={selectedCompany}
        refreshCompanies={fetchCompanys}
      />
    </Container>
  );
};

export default Company;